<template>
  <JadeFloating
    v-bind="$attrs"
    :open="open"
    :closeOn="closeOn"
    :zIndex="zIndex"
    :allowOutsideClick="false"
    dimmable
    @toggle="() => $emit('toggle')"
  >
    <div
      :class="computedClassModal"
      tabindex="-1"
      style="
        opacity: 1;
        transition-property: opacity;
        transition-duration: 250ms;
      "
    >
      <div
        v-if="!hideCloseButton"
        class="jade-modal__close"
      >
        <JadeIconButton
          icon="close"
          variant="neutral-ghost"
          size="medium"
          @click="() => $emit('onClose')"
        />
      </div>

      <div
        v-if="title"
        class="jade-modal__header"
      >
        <JadeText
          variant="heading-xsmall"
          color="current-color"
        >
          {{ title }}
        </JadeText>
      </div>

      <div class="jade-modal__main">
        <JadeAspectRatio
          v-if="$slots.cover"
          as="div"
          aspectRatio="wide"
        >
          <slot name="cover" />
        </JadeAspectRatio>

        <div
          v-if="$slots.default"
          :class="computedClassContent"
        >
          <slot />
        </div>
      </div>

      <div
        v-if="$slots.actionsGroup || $slots.actions"
        class="jade-modal__footer"
      >
        <div
          v-if="$slots.actionsGroup"
          :class="computedClassActionsGroup"
        >
          <slot name="actionsGroup" />
        </div>

        <template v-if="$slots.actions">
          <div class="jade-modal__footer-actions-mobile">
            <slot name="actions" />
          </div>

          <div class="jade-modal__footer-actions-desktop">
            <slot name="actions" />
          </div>
        </template>
      </div>
    </div>
  </JadeFloating>
</template>

<script>
import JadeFloating from '../JadeFloating'
import JadeIconButton from '../JadeIconButton'
import JadeAspectRatio from '../JadeAspectRatio'
import JadeText from '../JadeText'

import DIRECTION_ENUM from '../enums/direction.enum'
import ResizeMixin from '../utils/resize.mixin'

export default {
  name: 'JadeModal',
  mixins: [ResizeMixin],

  components: {
    JadeFloating,
    JadeIconButton,
    JadeAspectRatio,
    JadeText
  },

  props: {
    title: String,
    open: Boolean,
    paddingless: Boolean,
    hideCloseButton: Boolean,
    closeOn: {
      type: Array,
      default: () => ['esc', 'outside']
    },
    height: {
      type: String,
      default: 'auto',
      validation: h => ['auto', 'fixed'].includes(h)
    },
    zIndex: {
      type: Number,
      default: 1000
    },
    actionsOrientation: {
      type: String,
      validation: d => DIRECTION_ENUM.includes(d)
    }
  },

  computed: {
    computedOrientation () {
      return this.actionsOrientation || this.isMediumScreen
        ? DIRECTION_ENUM.HORIZONTAL
        : DIRECTION_ENUM.VERTICAL
    },
    computedClassModal () {
      return {
        'jade-modal': true,
        open: this.open,
        [`is-${this.height}`]: this.height,
        [this.isMediumScreen ? 'fade' : 'bottom-to-up']: true
      }
    },
    computedClassContent () {
      return {
        'jade-modal__content': true,
        'is-paddingless': this.paddingless
      }
    },
    computedClassActionsGroup () {
      return {
        'jade-modal__button-group-wrapper': this.$slots.actionsGroup,
        [`is-${this.computedOrientation}`]: this.computedOrientation,
        'is-desktop': this.isMediumScreen
      }
    }
  }
}
</script>

<style scoped>
@import '@stone-payments/jade/css/modal.css';

@keyframes fadeInitial {
  from {
    opacity: 0;
  }
  to {
    opacity: 0;
  }
}

@keyframes fadeOpen {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Bottom to Up Animation */
@keyframes bottomToUpInitial {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(100%);
  }
}

@keyframes bottomToUpOpen {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.fade {
  animation: fadeInitial 0.5s;
}

.fade.open {
  animation-name: fadeOpen;
}

.bottom-to-up {
  animation: bottomToUpInitial 0.5s;
}

.bottom-to-up.open {
  animation-name: bottomToUpOpen;
}
</style>
